import { createFileRoute } from "@tanstack/react-router";
import { Download, CheckCircle2 } from "lucide-react";

import { Reveal } from "@/components/site/Reveal";
import { CtaPanel, PageHero } from "@/components/site/Sections";
import { PricingGrid } from "@/components/site/Pricing";
import { PricingComparison } from "@/components/site/PricingComparison";
import { FullTimeVsFractional } from "@/components/site/FullTimeVsFractional";
import { StartupPricingCallout } from "@/components/site/StartupPricingCallout";
import { LeadMagnet } from "@/components/site/LeadMagnet";
import { PricingFaq } from "@/components/site/PricingFaq";
import { ProposalForm } from "@/components/site/ProposalForm";
import { pricingFaqs, pricingPlans } from "@/lib/finendra-data";
import {
  generateServiceSchema,
  generateFaqSchema,
  generateBreadcrumbSchema,
} from "@/components/site/Schema";

export const Route = createFileRoute("/pricing")({
  head: () => ({
    meta: [
      { title: "US Bookkeeping & Accounting Pricing: Starting from $199/mo | Finendra" },
      {
        name: "description",
        content:
          "Dedicated US bookkeeping starting from $199/mo for businesses that don't need a full-time bookkeeper, with Growth (from $499/mo) and Scale (from $1,299/mo) plans. Zero hourly billing.",
      },
      {
        property: "og:title",
        content: "US Bookkeeping & Accounting Pricing: Starting from $199/mo | Finendra",
      },
      {
        property: "og:description",
        content:
          "Fixed monthly packages starting from $199/mo for small businesses and growing teams. Replaces a $65,000/yr full-time in-house hire.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://finendra.com/pricing" },
      { property: "og:image", content: "https://finendra.com/og/og-pricing.png" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:image", content: "https://finendra.com/og/og-pricing.png" },
    ],
    links: [{ rel: "canonical", href: "https://finendra.com/pricing" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          generateServiceSchema({
            name: "Finendra Financial Operations & Bookkeeping",
            description:
              "Outcome-based US bookkeeping and finance operations starting from $199 per month for businesses that do not need a full-time bookkeeper.",
            priceRange: "$199 - $1299/month",
            url: "https://finendra.com/pricing",
          }),
        ),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          generateFaqSchema(
            pricingFaqs.flatMap((group) =>
              group.items.map((item) => ({
                q: item.q,
                a: item.a,
              })),
            ),
          ),
        ),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          generateBreadcrumbSchema([
            { name: "Home", url: "https://finendra.com" },
            { name: "Pricing", url: "https://finendra.com/pricing" },
          ]),
        ),
      },
    ],
  }),
  component: PricingPage,
});

function PricingPage() {
  return (
    <>
      <PageHero
        eyebrow="Transparent Monthly Pricing"
        title="Expert US Bookkeeping starting from $199/mo. Zero fluff."
        body="No full-time salary overhead ($65,000+/year). No surprise hourly bills. Fixed monthly retainers designed for small businesses that don't need a full-time bookkeeper, all the way to companies closing by the 5th."
      />

      {/* 4 Outcome-Based Tiers */}
      <section className="px-6 pb-12">
        <PricingGrid />
      </section>

      {/* Pricing PDF Download */}
      <section className="px-6 pb-16">
        <div className="mx-auto flex max-w-6xl justify-center">
          <Reveal>
            <a
              href="/assets/monthly-close-checklist.pdf"
              download="Finendra-Monthly-Close-Checklist-Founders.pdf"
              className="inline-flex items-center gap-2 rounded-full border border-brand-navy/15 bg-background px-6 py-3 text-sm font-semibold text-brand-navy shadow-xs transition-[border-color,box-shadow,transform] duration-150 ease-out hover:-translate-y-0.5 hover:border-brand-accent/40 hover:shadow-md active:scale-[0.975] active:duration-75 motion-reduce:transform-none select-none"
            >
              <Download className="h-4 w-4 text-brand-accent" aria-hidden="true" />
              Download Founder Monthly Close Checklist PDF
            </a>
          </Reveal>
        </div>
      </section>

      {/* Full-Time vs Fractional Comparison */}
      <FullTimeVsFractional />

      {/* Startup Pricing Callout */}
      <StartupPricingCallout slug="pricing" />

      {/* Comparison Matrix */}
      <section className="px-6 py-24 bg-background">
        <PricingComparison />
      </section>

      {/* Lead Magnet */}
      <section className="px-6 py-12">
        <LeadMagnet defaultIndustry="General Startup Pricing" />
      </section>

      {/* Custom Scoping / Proposal Form */}
      <section className="bg-brand-surface px-6 py-24 border-y border-brand-navy/5">
        <ProposalForm />
      </section>

      {/* Pricing FAQs with Startup Additions */}
      <section className="px-6 py-24">
        <PricingFaq />
      </section>

      <CtaPanel
        title="Not sure which plan fits your stage?"
        body="Tell us your current MRR and transaction volume. We'll recommend the exact right plan and deliver an audit-ready transition roadmap."
      />
    </>
  );
}
